/**
 * Waits for an element to appear inside a specific parent (observee) before resolving a promise.
 * @param {String} querySelector - CSS selector of the element to wait for.
 * @param {Number} timeout - Milliseconds to wait before timing out (0 for no timeout).
 * @param {String} [observee] - Optional selector of the parent element to observe.
 * @returns {Promise<Element>} - Resolves when the element is found, rejects if it times out.
 */
export const waitForElement = (querySelector: string, timeout: number, observee?: string) => {
    
    let timer: NodeJS.Timeout;

    return new Promise<Element>((resolve, reject) => {
        // Get the parent element (observee) or default to `document.body`
        let observeeElement = observee ? document.querySelector(observee) : document.body;

        // If the observee doesn't exist, make document.body as observee
        if (!observeeElement) {
            observeeElement = document.body;
        }

        /**
         * Function to check if the target element exists inside the observee
         */
        const checkElement = () => {
            const foundElement = observeeElement?.querySelector(querySelector);
            return foundElement || null;
        };

        // Check if the element is already present inside the observee
        let element = checkElement();
        if (element) return resolve(element);

        // MutationObserver to watch for changes in the observee
        const observer = new MutationObserver(() => {
            element = checkElement();
            if (element) {
                observer.disconnect(); // Stop observing once the element is found
                if (timer) clearTimeout(timer); // Clear timeout
                return resolve(element);
            }
        });

        // Start observing changes in the observee
        observer.observe(observeeElement, {
            childList: true, // Watch for added/removed elements
            subtree: true // Watch all descendants of observee
        });

        // If timeout is set, reject the promise if element doesn't appear in time
        if (timeout) {
            timer = setTimeout(() => {
                observer.disconnect(); // Stop observing
                reject(new Error(`Element '${querySelector}' did not appear inside '${observee}' within ${timeout/1000} secs`));
            }, timeout);
        }
    });
};
